import type { PriceObservation } from '@recipes/contracts';
import type { PriceChange } from '@recipes/domain';
import { cn } from '@recipes/ui';
import { TrendingDown, TrendingUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatDay, formatMoney, formatMoneyDelta } from './hooks';

const signed = (percent: string): string =>
  percent.startsWith('-') ? `−${percent.slice(1)}` : `+${percent}`;

/**
 * A price change in words and an arrow (docs/partial/shopping-in-store-spec.md 5.7, 11): the
 * direction is never conveyed by colour alone, and the figure carries its evidence.
 */
export function PriceChangeNote({
  change,
  compact = false,
  className,
}: {
  change: PriceChange<PriceObservation>;
  /** Arrow and amount only – for a list row. */
  compact?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const currency = change.current.currency;
  const up = change.direction === 'UP';
  const Icon = up ? TrendingUp : TrendingDown;
  const amount =
    change.basis === 'UNIT_PRICE'
      ? t('prices.change.unitPrice', { percent: signed(change.percent) })
      : `${formatMoneyDelta(change.absolute, currency)} (${signed(change.percent)} %)`;
  const evidence = t('prices.change.evidence', {
    before: formatMoney(change.previous.price, currency),
    beforeDate: formatDay(change.previous.observedAt),
    after: formatMoney(change.current.price, currency),
    afterDate: formatDay(change.current.observedAt),
  });
  return (
    <span
      className={cn('inline-flex flex-wrap items-center gap-1 text-xs', className)}
      title={evidence}
      data-testid="price-change"
      data-direction={change.direction}
    >
      <Icon aria-hidden className="size-3.5" />
      <span className="sr-only">{up ? t('prices.change.up') : t('prices.change.down')}</span>
      <span>{compact ? formatMoneyDelta(change.absolute, currency) : amount}</span>
      {!compact && <span>{t('prices.change.since', { date: formatDay(change.since) })}</span>}
      {!compact && change.packageChange && (
        <span className="basis-full" data-testid="price-change-package">
          {t('prices.change.package', {
            from: `${change.packageChange.from.quantity} ${change.packageChange.from.unitCode}`,
            to: `${change.packageChange.to.quantity} ${change.packageChange.to.unitCode}`,
          })}
        </span>
      )}
    </span>
  );
}

/** A small history of one store's prices: points over time, promotions hollow. */
export function PriceSparkline({
  observations,
  label,
}: {
  observations: readonly PriceObservation[];
  label: string;
}) {
  if (observations.length < 2) return null;
  const sorted = [...observations].sort((a, b) => a.observedAt.localeCompare(b.observedAt));
  const days = sorted.map((o) => Date.parse(`${o.observedAt}T12:00:00Z`));
  const values = sorted.map((o) => Number(o.price));
  const [minDay, maxDay] = [Math.min(...days), Math.max(...days)];
  const [minValue, maxValue] = [Math.min(...values), Math.max(...values)];
  const x = (day: number) =>
    maxDay === minDay ? 60 : 4 + ((day - minDay) / (maxDay - minDay)) * 112;
  const y = (value: number) =>
    maxValue === minValue ? 16 : 28 - ((value - minValue) / (maxValue - minValue)) * 24;
  const regular = sorted.flatMap((o, index) => (o.isPromotion ? [] : [index]));
  return (
    <svg
      viewBox="0 0 120 32"
      role="img"
      aria-label={label}
      className="h-8 w-32 text-primary"
      data-testid="price-sparkline"
    >
      <polyline
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        points={regular.map((index) => `${x(days[index]!)},${y(values[index]!)}`).join(' ')}
      />
      {sorted.map((o, index) => (
        <circle
          key={o.id}
          cx={x(days[index]!)}
          cy={y(values[index]!)}
          r="2"
          fill={o.isPromotion ? 'none' : 'currentColor'}
          stroke="currentColor"
        />
      ))}
    </svg>
  );
}
